/**
 * YunShl Behavior Tracker SDK (PC Shop)
 * 行为追踪SDK - 用于PC商城前端埋点
 *
 * 功能：
 * 1. 商品浏览追踪
 * 2. 搜索热词追踪
 * 3. 加购行为追踪
 * 4. 页面停留时长计算
 * 5. 批量上报功能
 */

import StorageUtil from './storage-util';
import {LocalConst} from './constant';
import UrlUtil from './url-config';
import CommonUtil from './common-util';
import md5 from 'md5';

const reqKey = "yunshang";
const API = UrlUtil.getAPI();

// 事件类型常量
export const BehaviorEventType = {
    GOODS_VIEW: 'goods_view',           // 商品浏览
    PAGE_VIEW: 'page_view',             // 页面浏览
    PAGE_STAY: 'page_stay',             // 页面停留
    SEARCH: 'search',                   // 搜索
    ADD_TO_CART: 'add_to_cart',         // 加购
    CART_MODIFY: 'cart_modify',         // 购物车修改
    CART_DELETE: 'cart_delete',         // 购物车删除
    ORDER_CREATE: 'order_create',       // 下单
    GOODS_FAVORITE: 'goods_favorite',   // 收藏
};

// 页面类型常量
export const PageType = {
    HOME: 1,            // 首页
    CATEGORY: 2,        // 分类页
    GOODS_DETAIL: 3,    // 商品详情
    SEARCH_RESULT: 4,   // 搜索结果
};

// 设备类型常量
export const DeviceType = {
    APP: 1,
    PC: 2,
    H5: 3,
    MINI_PROGRAM: 4,
};

/**
 * YunShlTracker - 行为追踪SDK
 */
const YunShlTracker = {
    // 配置
    config: {
        apiUrl: '',                    // 上报API地址
        batchSize: 10,                 // 批量上报数量阈值
        flushInterval: 5000,           // 上报间隔(ms)
        sessionTimeout: 30 * 60 * 1000, // 会话超时30分钟
        enabled: true,                 // 是否启用
        autoFlush: true,               // 是否自动上报
    },

    // 状态
    state: {
        sessionId: null,               // 会话ID
        deviceId: null,                // 设备ID
        userId: null,                  // 用户ID
        clientId: null,                // 客户ID
        companyId: null,               // 企业ID
        queue: [],                     // 待上报事件队列
        pageEnterTime: null,           // 页面进入时间
        currentPage: null,             // 当前页面信息
        flushTimer: null,              // 上报定时器
        authFailed: false,             // 认证失败标记(401时停止上报)
        networkFailCount: 0,           // 网络失败计数
        maxNetworkFailCount: 3,        // 最大网络失败次数
    },

    /**
     * 初始化SDK
     * @param {Object} options - 配置选项
     */
    init(options = {}) {
        this.config = {...this.config, ...options};
        this.config.apiUrl = this.config.apiUrl || `${API}/v1/shop/behavior`;

        // 生成/恢复会话ID
        this._initSession();

        // 生成/恢复设备ID
        this._initDeviceId();

        // 获取用户信息
        this._loadUserInfo();

        // 绑定页面事件
        this._bindPageEvents();

        // 启动定时上报
        this._startFlushTimer();

        console.log('[YunShlTracker] initialized', {
            sessionId: this.state.sessionId,
            deviceId: this.state.deviceId,
            enabled: this.config.enabled
        });
    },

    /**
     * 设置用户信息（登录后调用）
     */
    setUser(userId, clientId, companyId) {
        this.state.userId = userId;
        this.state.clientId = clientId;
        this.state.companyId = companyId;

        // 登录后重置认证失败状态并恢复上报
        if (this.state.authFailed || this.state.networkFailCount >= this.state.maxNetworkFailCount) {
            this.state.authFailed = false;
            this.state.networkFailCount = 0;
            this._startFlushTimer();
            console.log('[YunShlTracker] auth restored, resume tracking');
        }
    },

    /**
     * 追踪事件
     */
    track(eventType, eventData = {}) {
        if (!this.config.enabled) return;

        // 动态获取最新的用户信息
        this._loadUserInfo();

        const event = {
            event_type: eventType,
            event_data: eventData,
            session_id: this.state.sessionId,
            device_id: this.state.deviceId,
            user_id: this.state.userId,
            client_id: this.state.clientId,
            company_id: this.state.companyId,
            device_type: this._getDeviceType(),
            timestamp: Date.now()
        };
        this.state.queue.push(event);

        // 达到批量数量立即上报
        if (this.state.queue.length >= this.config.batchSize) {
            this._flush();
        }
    },

    /**
     * 追踪商品浏览
     */
    trackGoodsView(goodsId, categoryId, stayDuration = 0) {
        this.track(BehaviorEventType.GOODS_VIEW, {
            goods_id: goodsId,
            category_id: categoryId,
            page_type: PageType.GOODS_DETAIL,
            stay_duration: stayDuration
        });
    },

    /**
     * 追踪搜索
     */
    trackSearch(keywords, resultCount, searchType = 1) {
        this.track(BehaviorEventType.SEARCH, {
            keywords: keywords,
            result_count: resultCount,
            search_type: searchType
        });
    },

    /**
     * 追踪加购
     */
    trackAddToCart(goodsId, productId, number, price) {
        this.track(BehaviorEventType.ADD_TO_CART, {
            goods_id: goodsId,
            product_id: productId,
            number: number,
            price: price,
            action_type: 1
        });
    },

    /**
     * 追踪购物车修改
     */
    trackCartModify(goodsId, productId, oldNumber, newNumber, price) {
        this.track(BehaviorEventType.CART_MODIFY, {
            goods_id: goodsId,
            product_id: productId,
            old_number: oldNumber,
            new_number: newNumber,
            price: price,
            action_type: 2
        });
    },

    /**
     * 追踪购物车删除
     */
    trackCartDelete(goodsId, productId, number) {
        this.track(BehaviorEventType.CART_DELETE, {
            goods_id: goodsId,
            product_id: productId,
            number: number,
            action_type: 3
        });
    },

    /**
     * 追踪下单
     */
    trackOrderCreate(orderId, amount) {
        this.track(BehaviorEventType.ORDER_CREATE, {
            order_id: orderId,
            amount: amount
        });
    },

    /**
     * 追踪收藏
     */
    trackGoodsFavorite(goodsId, isFavorite) {
        this.track(BehaviorEventType.GOODS_FAVORITE, {
            goods_id: goodsId,
            is_favorite: isFavorite ? 1 : 0
        });
    },

    /**
     * 追踪页面浏览
     */
    trackPageView(pageType, pageUrl) {
        this.track(BehaviorEventType.PAGE_VIEW, {
            page_type: pageType,
            page_url: pageUrl || window.location.pathname,
            referrer: document.referrer
        });
    },

    /**
     * 追踪页面停留时长
     */
    trackPageStay(pageType, stayDuration) {
        this.track(BehaviorEventType.PAGE_STAY, {
            page_type: pageType,
            stay_duration: stayDuration
        });
    },

    /**
     * 手动上报
     */
    flush() {
        this._flush();
    },

    /**
     * 设置启用状态
     */
    setEnabled(enabled) {
        this.config.enabled = enabled;
    },

    // ==================== 私有方法 ====================

    /**
     * 初始化会话
     */
    _initSession() {
        const sessionKey = '__ys_sid';
        const lastSessionTime = localStorage.getItem('__ys_sid_time');
        const now = Date.now();

        // 检查会话是否超时
        if (lastSessionTime && (now - parseInt(lastSessionTime)) < this.config.sessionTimeout) {
            this.state.sessionId = localStorage.getItem(sessionKey);
        }

        // 创建新会话
        if (!this.state.sessionId) {
            this.state.sessionId = this._generateId();
            localStorage.setItem(sessionKey, this.state.sessionId);
        }

        localStorage.setItem('__ys_sid_time', now.toString());
    },

    /**
     * 初始化设备ID
     */
    _initDeviceId() {
        const deviceKey = '__ys_did';
        this.state.deviceId = localStorage.getItem(deviceKey) || this._generateId();
        localStorage.setItem(deviceKey, this.state.deviceId);
    },

    /**
     * 加载用户信息
     */
    _loadUserInfo() {
        const ysUser = StorageUtil.getItem(LocalConst.mShop_user) || {};
        const companyId = StorageUtil.getItem(LocalConst.companyId);
        this.state.userId = ysUser.id_;
        this.state.clientId = ysUser.client_id_;
        this.state.companyId = companyId || ysUser.company_id_;
    },

    /**
     * 绑定页面事件
     */
    _bindPageEvents() {
        const self = this;

        // 页面可见性变化
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                self._recordPageStay();
            } else {
                self.state.pageEnterTime = Date.now();
            }
        });

        // 页面关闭前上报
        window.addEventListener('beforeunload', () => {
            self._recordPageStay();
            self._flush();
        });

        // 记录页面进入时间
        this.state.pageEnterTime = Date.now();
    },

    /**
     * 记录页面停留时长
     */
    _recordPageStay() {
        if (this.state.pageEnterTime && this.state.currentPage) {
            const duration = Math.floor((Date.now() - this.state.pageEnterTime) / 1000);
            if (duration > 0) {
                this.trackPageStay(this.state.currentPage.pageType, duration);
            }
        }
    },

    /**
     * 启动定时上报
     */
    _startFlushTimer() {
        if (!this.config.autoFlush) return;

        if (this.state.flushTimer) {
            clearInterval(this.state.flushTimer);
        }
        this.state.flushTimer = setInterval(() => {
            this._flush();
        }, this.config.flushInterval);
    },

    /**
     * 批量上报
     */
    _flush() {
        // 认证失败或网络连续失败超过阈值时停止上报
        if (this.state.authFailed || this.state.networkFailCount >= this.state.maxNetworkFailCount) return;

        if (this.state.queue.length === 0) return;

        const events = this.state.queue.slice();
        this.state.queue = [];

        const data = {
            events: events,
            session_id: this.state.sessionId,
            device_id: this.state.deviceId,
            device_type: this._getDeviceType()
        };

        this._xhrFlush(data, events);
    },

    /**
     * XHR上报
     */
    _xhrFlush(data, events) {
        const token = StorageUtil.getItem(LocalConst.token);
        const timestamp = CommonUtil.getNonce();
        const nonce = CommonUtil.getRandomParam();

        const headers = {
            'Content-Type': 'application/json;charset=utf-8',
            'yunshltoken': token || '',
            'timestamp': timestamp,
            'nonce': nonce,
            'tokentype': 'PC',
            'appsource': 'PC',
        };

        // 签名
        const checkKey = reqKey + headers.yunshltoken + timestamp + nonce + JSON.stringify(data);
        headers.signnatrue = md5(checkKey);

        fetch(`${this.config.apiUrl}/track/batch`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                console.log('[YunShlTracker] flush success, events:', events.length);
                // 上报成功，重置网络失败计数
                this.state.networkFailCount = 0;
            } else if (response.status === 401) {
                // 401认证失败，停止上报定时器，避免无限重试
                console.warn('[YunShlTracker] auth failed (401), stop tracking until login');
                this.state.authFailed = true;
                this.state.queue = []; // 清空队列，避免堆积
                if (this.state.flushTimer) {
                    clearInterval(this.state.flushTimer);
                    this.state.flushTimer = null;
                }
            } else {
                console.warn('[YunShlTracker] flush failed, status:', response.status);
                // 非401错误才恢复队列重试
                this.state.queue = events.concat(this.state.queue);
            }
        }).catch(err => {
            console.warn('[YunShlTracker] flush error:', err);
            // 网络错误时恢复队列并计数
            this.state.queue = events.concat(this.state.queue);
            this.state.networkFailCount++;
            // 连续失败超过阈值，停止上报
            if (this.state.networkFailCount >= this.state.maxNetworkFailCount) {
                console.warn('[YunShlTracker] network failed 3 times, stop tracking');
                if (this.state.flushTimer) {
                    clearInterval(this.state.flushTimer);
                    this.state.flushTimer = null;
                }
            }
        });
    },

    /**
     * 生成UUID
     */
    _generateId() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    },

    /**
     * 获取设备类型
     */
    _getDeviceType() {
        return DeviceType.PC;
    },

    /**
     * 获取页面类型
     */
    _getPageType() {
        const path = window.location.pathname;
        if (/\/goods\/detail/.test(path)) return PageType.GOODS_DETAIL;
        if (/\/goods\/list/.test(path)) return PageType.SEARCH_RESULT;
        if (/\/goods/.test(path)) return PageType.CATEGORY;
        if (path === '/' || /\/home/.test(path)) return PageType.HOME;
        return 0;
    }
};

// 挂载到全局window对象
if (typeof window !== 'undefined') {
    window.YunShlTracker = YunShlTracker;
}

export default YunShlTracker;
